/* global BigInt */
import React, { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { FaMoneyBillWave, FaCalculator, FaCrown, FaUserShield } from "react-icons/fa";

// Import ABI z src/abi
import CRWABI from "../abi/Crown.json";
import CUSDABI from "../abi/CUSD.json";
import DEUSABI from "../abi/DEUS.json";

// Adresy kontraktů
const CRW_ADDRESS = "0xcEe6450FD8797C10Ec6b6C64784ed4EAB5470e03";
const CUSD_ADDRESS = "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa";
const DEUS_ADDRESS = "0x9a1EE4cC684b75eb847F2CCBDf7DeB26F8f8D6F5";

const MyAccount = ({ provider, account }) => {
    const [balances, setBalances] = useState({ CRW: "0", CUSD: "0", DEUS: "0" });
    const [selectedToken, setSelectedToken] = useState("CRW");
    const [stakingRewards, setStakingRewards] = useState("0");
    const [validatorRewards, setValidatorRewards] = useState("0");
    const [stakeResult, setStakeResult] = useState("0");
    const [dailyRewardRate, setDailyRewardRate] = useState("0");
    const [stakeAmount, setStakeAmount] = useState("");
    const [stakeDuration, setStakeDuration] = useState("30");

    const calculateStake = useCallback((amount, durationDays, rate) => {
        console.log("Calculating with dailyRewardRate:", rate);
        if (!amount || isNaN(amount) || amount <= 0 || !durationDays || isNaN(durationDays) || rate <= 0) {
            console.log("Invalid inputs or zero dailyRewardRate, setting result to 0", { amount, durationDays, rate });
            setStakeResult("0");
            return;
        }

        try {
            const amountInWei = BigInt(ethers.parseUnits(amount.toString(), 18));
            const timeElapsed = BigInt(parseInt(durationDays) * 24 * 60 * 60); // Sekundy
            const dailyRewardRateInWei = BigInt(Math.round(rate * 10000)); // Zaokrouhlení pro přesnost
            console.log("dailyRewardRateInWei:", dailyRewardRateInWei.toString());
            const rewardsInWei = (amountInWei * dailyRewardRateInWei * timeElapsed) / BigInt(10000 * 24 * 60 * 60);
            const rewardsInUnits = ethers.formatUnits(rewardsInWei.toString(), 18);
            setStakeResult(parseFloat(rewardsInUnits).toFixed(2));
        } catch (error) {
            console.error("Calculation error:", error);
            setStakeResult("0");
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!provider || !account) return;

            try {
                const crwContract = new ethers.Contract(CRW_ADDRESS, CRWABI, provider);
                const crwBalance = await crwContract.balanceOf(account);
                const pendingRewards = await crwContract.getPendingRewards(account);
                let dailyRate = await crwContract.DAILY_REWARD_RATE();
                console.log("Daily Reward Rate (raw):", dailyRate.toString());
                dailyRate = BigInt(dailyRate);
                const formattedRate = Number(dailyRate) / 10000; // Přesnost 10^4
                if (formattedRate === 0) {
                    console.error("Error: DAILY_REWARD_RATE is 0 in contract");
                    setDailyRewardRate(0);
                } else {
                    console.log("Formatted dailyRewardRate:", formattedRate);
                    setDailyRewardRate(formattedRate); // Použijeme hodnotu z kontraktu
                }

                const cusdContract = new ethers.Contract(CUSD_ADDRESS, CUSDABI, provider);
                const cusdBalance = await cusdContract.balanceOf(account);

                const deusContract = new ethers.Contract(DEUS_ADDRESS, DEUSABI, provider);
                const deusBalance = await deusContract.balanceOf(account);

                const [validators, , validatorRewardsArray] = await crwContract.getValidators();
                console.log("Validators:", validators);
                console.log("Validator Rewards Array:", validatorRewardsArray);
                let userValidatorRewards = "0";
                const index = validators.findIndex((addr) => addr.toLowerCase() === account.toLowerCase());
                if (index !== -1) {
                    userValidatorRewards = ethers.formatUnits(validatorRewardsArray[index], 18);
                    console.log("User Validator Rewards:", userValidatorRewards);
                }

                setBalances({
                    CRW: ethers.formatUnits(crwBalance, 18),
                    CUSD: ethers.formatUnits(cusdBalance, 18),
                    DEUS: ethers.formatUnits(deusBalance, 18),
                });
                setStakingRewards(ethers.formatUnits(pendingRewards, 18));
                setValidatorRewards(userValidatorRewards);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();

        if (provider && account) {
            const crwContract = new ethers.Contract(CRW_ADDRESS, CRWABI, provider);
            const handleRewardsWithdrawn = (validator, amount) => {
                if (validator.toLowerCase() === account.toLowerCase()) {
                    fetchData(); // Refresh dat po výběru odměn
                }
            };
            const handleDailyRewardRateUpdated = (oldRate, newRate) => {
                console.log("Daily Reward Rate Updated:", newRate.toString());
                fetchData(); // Refresh dat po změně
            };
            crwContract.on("RewardsWithdrawn", handleRewardsWithdrawn);
            crwContract.on("DailyRewardRateUpdated", handleDailyRewardRateUpdated);

            return () => {
                crwContract.off("RewardsWithdrawn", handleRewardsWithdrawn);
                crwContract.off("DailyRewardRateUpdated", handleDailyRewardRateUpdated);
            };
        }
    }, [provider, account]);

    useEffect(() => {
        if (dailyRewardRate > 0 && stakeAmount && stakeDuration) {
            calculateStake(stakeAmount, stakeDuration, dailyRewardRate);
        }
    }, [stakeAmount, stakeDuration, dailyRewardRate, calculateStake]);

    const formatNumber = (num) => {
        const parsedNum = parseFloat(num);
        if (isNaN(parsedNum)) return "0";
        return parsedNum.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <div className="main-content my-account">
            <h2>
                <FaCrown className="icon" /> My Account
            </h2>
            <div className="overview-grid my-account">
                <div className="action-box">
                    <h3><FaMoneyBillWave className="icon" /> Total Balance</h3>
                    <select
                        id="balanceToken"
                        value={selectedToken}
                        onChange={(e) => setSelectedToken(e.target.value)}
                    >
                        <option value="CRW">CRW</option>
                        <option value="CUSD">CUSD</option>
                        <option value="DEUS">DEUS</option>
                    </select>
                    <p>{formatNumber(balances[selectedToken])} {selectedToken}</p>
                </div>
                <div className="action-box">
                    <h3><FaMoneyBillWave className="icon" /> Staking Rewards</h3>
                    <p>{formatNumber(stakingRewards)} CRW</p>
                </div>
                <div className="action-box">
                    <h3><FaUserShield className="icon" /> Validator Rewards</h3>
                    <p>{formatNumber(validatorRewards)} CRW</p>
                </div>
                <div className="action-box">
                    <h3><FaCalculator className="icon" /> Staking Calculator</h3>
                    <p className="daily-reward-rate">Daily Reward Rate: {(dailyRewardRate * 100).toFixed(2)}% ({(dailyRewardRate * 100 * 30).toFixed(2)}% monthly)</p>
                    <input
                        id="stakeAmount"
                        type="number"
                        placeholder="Amount (e.g., 100 CRW)"
                        min="0.01"
                        step="0.01"
                        value={stakeAmount}
                        onChange={(e) => {
                            const value = e.target.value;
                            setStakeAmount(value);
                        }}
                    />
                    <select
                        id="stakeDuration"
                        value={stakeDuration}
                        onChange={(e) => {
                            const value = e.target.value;
                            setStakeDuration(value);
                        }}
                    >
                        <option value="30">1 Month (30 days)</option>
                        <option value="180">6 Months (180 days)</option>
                        <option value="360">12 Months (360 days)</option>
                    </select>
                    <p>Estimated Rewards: {formatNumber(stakeResult)} CRW</p>
                </div>
            </div>
        </div>
    );
};

export default MyAccount;